import { useEffect, useRef, useState } from "react";

/**
 * Records the note as audio, for transcribing on the workstation.
 *
 * The phone's own dictation mishears food words — "mi goreng" came back as
 * "me gore and" — so the recording is what gets sent, and Whisper reads it
 * here with a vocabulary of the words that actually come up. The audio is kept,
 * so a bad transcript can be re-read rather than lost.
 *
 * It records 16 kHz mono WAV directly, which is what Whisper wants, so nothing
 * has to be converted on the way.
 */

const TARGET_RATE = 16_000;
const MAX_SECONDS = 180;

function encodeWav(samples: Float32Array, rate: number) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const text = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  text(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  text(8, "WAVEfmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, samples.length * 2, true);
  let o = 44;
  for (const s of samples) {
    const clamped = Math.max(-1, Math.min(1, s));
    view.setInt16(o, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    o += 2;
  }
  return new Blob([buffer], { type: "audio/wav" });
}

/** Averages groups of samples down to 16 kHz, which is gentler than dropping them. */
function downsample(input: Float32Array, from: number, to: number) {
  if (from === to) return input;
  const ratio = from / to;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    out[i] = sum / Math.max(1, end - start);
  }
  return out;
}

export function VoiceRecorder({ onRecorded }: { onRecorded: (wav: Blob, seconds: number) => void }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const chunks = useRef<Float32Array[]>([]);
  const stop = useRef<(() => void) | null>(null);

  useEffect(() => () => stop.current?.(), []);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  useEffect(() => {
    if (recording && seconds >= MAX_SECONDS) stop.current?.();
  }, [recording, seconds]);

  async function start() {
    setErr(null);
    chunks.current = [];
    setSeconds(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const node = ctx.createScriptProcessor(4096, 1, 1);
      node.onaudioprocess = (e) => {
        const input = e.inputBuffer.getChannelData(0);
        chunks.current.push(new Float32Array(input));
        let peak = 0;
        for (const s of input) peak = Math.max(peak, Math.abs(s));
        setLevel(peak);
      };
      source.connect(node);
      node.connect(ctx.destination);
      setRecording(true);

      stop.current = () => {
        node.disconnect();
        source.disconnect();
        stream.getTracks().forEach((t) => t.stop());
        const total = chunks.current.reduce((n, c) => n + c.length, 0);
        const merged = new Float32Array(total);
        let o = 0;
        for (const c of chunks.current) {
          merged.set(c, o);
          o += c.length;
        }
        const rate = ctx.sampleRate;
        void ctx.close();
        setRecording(false);
        setLevel(0);
        stop.current = null;
        const wav = encodeWav(downsample(merged, rate, TARGET_RATE), TARGET_RATE);
        if (merged.length > rate) onRecorded(wav, Math.round(merged.length / rate));
        else setErr("That was too short to send.");
      };
    } catch (e) {
      setErr(
        (e as Error).name === "NotAllowedError"
          ? "Microphone blocked. Allow it for this site in your browser settings."
          : (e as Error).message
      );
    }
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => (recording ? stop.current?.() : start())}
        className={recording ? "btn-accent px-3" : "btn-secondary px-3"}
      >
        {recording ? `◼ Stop ${seconds}s` : "🎙 Record"}
      </button>
      {recording && (
        <span className="h-2 w-16 overflow-hidden rounded-full bg-ink-line" aria-hidden="true">
          <span
            className="block h-2 rounded-full bg-accent transition-all duration-fast"
            style={{ width: `${Math.min(100, Math.round(level * 200))}%` }}
          />
        </span>
      )}
      {err && <span className="text-xs text-loss">{err}</span>}
    </div>
  );
}
